import { JsonObject } from '@bufbuild/protobuf';
import {
  AlertContainer,
  AlertRequest,
  AlertType,
  CardRequest,
} from '@q6cyber/proto2es/generated/alert';

import { FormControl, FormGroup, Validators } from '@angular/forms';

import { RouterOutput } from '../../../utils';
import { CustomFormArray, customFormGroup } from '../utilities';
import {
  AlphaNumericWithSeparators,
  celExpressionValidator,
  multipleInputValidator,
} from '../validations';
import { commentsForm } from './comments.interface';
import { CorruptedData } from './generic-data.interface';

export interface AlertBuilderListResponse
  extends
    Omit<RouterOutput['alertBuilder']['list']['edges'][0], 'node'>,
    CorruptedData {
  node: AlertContainer | null;
}

export const DEFAULT_ALERT_CONTAINER = new AlertContainer().toJson({
  emitDefaultValues: true,
}) as JsonObject;

export const DEFAULT_ALERT_REQUEST = new AlertRequest().toJson({
  emitDefaultValues: true,
}) as JsonObject;

export const DEFAULT_ALERT_REQUEST_FRAUD = new AlertRequest()
  .fromJson({
    type: AlertType[AlertType.TA_DIRECT],
    tagExistingData: false,
  })
  .toJson({
    emitDefaultValues: true,
  }) as JsonObject;

export const DEFAULT_CARD_REQUEST = new CardRequest()
  .fromJson({
    sources: {
      include: [],
      exclude: [],
    },
    clients: {
      include: [],
      exclude: [],
    },
    bins: {
      include: [],
      exclude: [],
    },
    status: {
      include: [],
      exclude: [],
    },
    baseName: {
      include: [],
      exclude: [],
    },
    cardSources: {
      include: [],
      exclude: [],
    },
    priceRange: {
      gte: '',
      lte: '',
      lt: '',
      gt: '',
    },
    firstSeenRange: {
      gte: '',
      lte: '',
      lt: '',
      gt: '',
    },
    fetchDateRange: {
      gte: '',
      lte: '',
      lt: '',
      gt: '',
    },
  })
  .toJson({
    emitDefaultValues: true,
  }) as JsonObject;

export type AlertBuilderFormGroup = ReturnType<typeof alertBuilderForm>;

export const alertBuilderForm = () => {
  const form = customFormGroup(
    {
      id: new FormControl(''),
      name: new FormControl('', [
        Validators.required,
        Validators.pattern(AlphaNumericWithSeparators),
      ]),
      type: new FormControl<string | null>(null, [Validators.required]),
      owner: new FormControl('', [Validators.required]),
      category: new FormControl<string[]>([], {
        validators: [Validators.required, Validators.minLength(1)],
        nonNullable: true,
      }),
      userGroup: new FormControl<string[]>([], {
        validators: [Validators.required],
        nonNullable: true,
      }),
      fraudClientId: new FormControl<string>(''),
      externallyManaged: new FormControl(false),
      message: new FormControl('', [Validators.maxLength(1000)]),
      tags: new FormControl<string[]>([], { nonNullable: true }),
      to: new FormControl<string[]>([], { nonNullable: true }),
      createdBy: new FormControl(''),
      lastEditedBy: new FormControl(''),
      comments: new CustomFormArray([], commentsForm),
      alertRequest: new CustomFormArray([], alertRequestFormGroup),
    },
    {
      errorMessages: [
        {
          fieldKey: 'name',
          errorName: 'required',
          message: 'Alert Container name is required',
        },
        {
          fieldKey: 'type',
          errorName: 'required',
          message: 'Alert Container type is required',
        },
        {
          fieldKey: 'userGroup',
          errorName: 'required',
          message: 'User Group is required',
        },
        {
          fieldKey: 'alertRequest.name',
          errorName: 'required',
          message: 'Alert name is required',
        },
        {
          fieldKey: 'alertRequest.type',
          errorName: 'required',
          message: 'Alert Type is required',
        },
        {
          fieldKey: 'to',
          errorName: 'invalidSlackAddress',
          message:
            'To field must be either a channel starting with (#) or a valid email address.',
        },
        {
          fieldKey: 'alertRequest.urls',
          errorName: 'invalidURLs',
          message: 'Alert URLs must be valid.',
        },
        {
          fieldKey: 'message',
          errorName: 'maxlength',
          message: e =>
            `Message should be less than ${e.error_value.requiredLength} characters.
          Currently ${e.error_value.actualLength}`,
        },
        {
          fieldKey: 'alertRequest.name',
          errorName: 'invalidPatternDashUnderscore',
          message:
            'Alert name must only contain letters, numbers, dash (-) or underscore (_)',
        },
        {
          fieldKey: 'alertRequest.advancedQuery',
          errorName: 'invalidQueryString',
          message: 'Advanced Query is not a valid JSON',
        },
        {
          fieldKey: 'alertRequest.filterQuery',
          errorName: 'invalidQueryString',
          message: 'Filter Query is not an array of valid JSONs',
        },
        {
          fieldKey: 'alertRequest.advancedCelQuery',
          errorName: 'invalidCelExpression',
          message: e =>
            `Advanced CEL Query: ${
              e.error_value?.message ?? 'is not a valid CEL expression'
            }`,
        },
        {
          fieldKey: 'alertRequest.cardRequest',
          errorName: 'invalidCardRequest',
          message: 'Card request field must contain an operation and a value.',
        },
        {
          fieldKey: 'alertRequest.eventTimeFilter',
          errorName: 'invalidTimeframeConfiguration',
          message: 'Invalid Timeframe configuration',
        },
        {
          fieldKey: 'name',
          errorName: 'invalidPatternDashUnderscore',
          message:
            'Alert Container name must only contain letters, numbers, dash (-) or underscore (_)',
        },
      ],
    }
  );

  return form;
};

export type AlertRequestFormGroup = ReturnType<typeof alertRequestFormGroup>;
export type AlertRequestCardsFormGroup = ReturnType<
  typeof cardRequestFormGroup
>;

export type FieldMapFormGroup = ReturnType<typeof fieldMapForm>;
export type FieldMapFormGroupValue = ReturnType<
  ReturnType<typeof fieldMapForm>['getRawValue']
>;

export const fieldMapForm = () => {
  return new FormGroup({
    key: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    operation: new FormControl('', Validators.required),
    values: new FormControl([] as string[] | string, Validators.required),
  });
};

export const alertRequestFormGroup = (alertRequest?: JsonObject) => {
  const formGroup = customFormGroup(
    {
      id: new FormControl(''),
      clientId: new FormControl(''),
      name: new FormControl('', [
        Validators.required,
        Validators.pattern(AlphaNumericWithSeparators),
      ]),
      requiredTerms: new FormControl([], { nonNullable: true }),
      optionalTerms: new FormControl([], { nonNullable: true }),
      excludeTerms: new FormControl([], { nonNullable: true }),
      urls: new FormControl([], { nonNullable: true }),
      tags: new FormControl([], { nonNullable: true }),
      fields: new FormControl([], { nonNullable: true }),
      to: new FormControl([], { nonNullable: true }),
      message: new FormControl(''),
      type: new FormControl<string | null>(null, [Validators.required]),
      tagExistingData: new FormControl(false),
      ranges: new FormControl(null),
      filterQuery: new FormControl([], { nonNullable: true }),
      advancedQuery: new FormControl(''),
      advancedCelQuery: new FormControl('', [celExpressionValidator]),
      cardRequest: cardRequestFormGroup(),
      eventTimeFilter: new FormControl(''),
      excludeSources: new FormControl([], { nonNullable: true }),
      excludeSourcePrefixes: new FormControl([], { nonNullable: true }),
    },
    undefined,
    multipleInputValidator([
      'urls',
      'requiredTerms',
      'optionalTerms',
      'cardRequest',
      'advancedCelQuery',
    ])
  );
  alertRequest && formGroup.patchValue(alertRequest);
  return formGroup;
};

const cardRequestFormGroup = () => {
  return new FormGroup({
    sources: includeExcludeTermsFormGroup(),
    clients: includeExcludeTermsFormGroup(),
    bins: includeExcludeTermsFormGroup(),
    status: includeExcludeTermsFormGroup(),
    baseName: includeExcludeTermsFormGroup(),
    cardSources: includeExcludeTermsFormGroup(),
    priceRange: rangeQueryFormGroup(),
    fetchDateRange: rangeQueryFormGroup(),
    firstSeenRange: rangeQueryFormGroup(),
  });
};

const rangeQueryFormGroup = () => {
  return new FormGroup({
    lt: new FormControl(''),
    lte: new FormControl(''),
    gt: new FormControl(''),
    gte: new FormControl(''),
  });
};

const includeExcludeTermsFormGroup = () => {
  return new FormGroup({
    include: new FormControl([], { nonNullable: true }),
    exclude: new FormControl([], { nonNullable: true }),
  });
};
